import {
  FP_CUSTOM_SOURCE_PREFIX,
  FP_FIELD_SOURCES,
  FP_LINE_FIELD_SOURCES,
  FP_SHOW_WHEN_ATTRIBUTE,
  FP_SHOW_WHEN_CONDITIONS,
  FP_TOTAL_WEIGHT_SOURCE_PREFIX,
  parseShowWhenValue,
} from "@forgepoint/shared";
import { parseDocumentHtml } from "./documentEditor";

/** The distinct data sources a document binds, in document order. */
export interface UsedSources {
  /** Quote-scope sources: standard keys, `custom:<name>`, `total-weight:<unit>`. */
  quoteSources: string[];
  /** Line-scope sources bound by `fp-line-field` inside repeaters. */
  lineSources: string[];
}

/** The sources gathered so far, before de-duplication and filtering. */
interface SourceAccumulator {
  quote: string[];
  line: string[];
}

/**
 * Scans a stored document for every data source it binds, so the sample-data
 * form can offer exactly the fields the template uses and nothing else.
 * Repeater interiors live inside `<template>`, which `querySelectorAll` does
 * not descend into, so the scan recurses into their content explicitly.
 * @param documentHtml - The stored document HTML
 * @returns The distinct quote-scope and line-scope sources, in document order
 */
export function collectUsedSources(documentHtml: string): UsedSources {
  const accumulator: SourceAccumulator = { quote: [], line: [] };
  collectFromRoot(parseDocumentHtml(documentHtml).body, false, accumulator);
  return {
    quoteSources: distinct(accumulator.quote).filter(isKnownQuoteSource),
    lineSources: distinct(accumulator.line).filter(isKnownLineSource),
  };
}

/**
 * Gathers the sources bound directly under one DOM root, then recurses into
 * every `<template>` it holds (repeater interiors, which nest freely).
 * @param root - The element or fragment to scan
 * @param inLineScope - True when the root is a repeater's template content
 * @param accumulator - The sources gathered so far, appended to in place
 * @returns Nothing; results land in the accumulator
 */
function collectFromRoot(
  root: ParentNode,
  inLineScope: boolean,
  accumulator: SourceAccumulator,
): void {
  root
    .querySelectorAll("fp-field[source], fp-markdown[source], fp-text[source]")
    .forEach((element) =>
      collectFieldBound(
        attributeValue(element, "source"),
        inLineScope,
        accumulator,
      ),
    );
  root
    .querySelectorAll("fp-line-field[source], fp-column[source]")
    .forEach((element) =>
      accumulator.line.push(attributeValue(element, "source")),
    );
  root.querySelectorAll("fp-lines[number-field]").forEach((element) => {
    const name = attributeValue(element, "number-field");
    if (name !== "") {
      accumulator.line.push(`${FP_CUSTOM_SOURCE_PREFIX}${name}`);
    }
  });
  root
    .querySelectorAll(`[${FP_SHOW_WHEN_ATTRIBUTE}]`)
    .forEach((element) =>
      collectShowWhen(
        attributeValue(element, FP_SHOW_WHEN_ATTRIBUTE),
        inLineScope,
        accumulator,
      ),
    );
  root
    .querySelectorAll("template")
    .forEach((template) =>
      collectFromRoot(template.content, true, accumulator),
    );
}

/**
 * Records a `source` bound by a field-bound tag, resolved at line scope first
 * inside a repeater.
 * @param source - The raw `source` attribute value
 * @param inLineScope - True when the element sits in a repeater's template
 * @param accumulator - The sources gathered so far, appended to in place
 * @returns Nothing; results land in the accumulator
 */
function collectFieldBound(
  source: string,
  inLineScope: boolean,
  accumulator: SourceAccumulator,
): void {
  if (inLineScope && isKnownLineSource(source)) {
    accumulator.line.push(source);
    return;
  }
  accumulator.quote.push(source);
}

/**
 * Records every condition of a `data-fp-show-when` value as a used source.
 * Named conditions read nothing off the sample data, so they are skipped;
 * everything else is a field source, resolved at line scope first inside a
 * repeater.
 * @param value - The raw `data-fp-show-when` attribute value
 * @param inLineScope - True when the element sits in a repeater's template
 * @param accumulator - The sources gathered so far, appended to in place
 * @returns Nothing; results land in the accumulator
 */
function collectShowWhen(
  value: string,
  inLineScope: boolean,
  accumulator: SourceAccumulator,
): void {
  parseShowWhenValue(value).forEach(({ condition }) => {
    if (condition === "" || isNamedCondition(condition)) {
      return;
    }
    if (inLineScope && isKnownLineSource(condition)) {
      accumulator.line.push(condition);
      return;
    }
    accumulator.quote.push(condition);
  });
}

/**
 * An element's attribute value, or "" when unset.
 * @param element - The element carrying the attribute
 * @param name - The attribute name
 * @returns The attribute value, or "" when unset
 */
function attributeValue(element: Element, name: string): string {
  return element.getAttribute(name) ?? "";
}

/**
 * De-duplicates values keeping first-seen order.
 * @param values - The raw values in document order
 * @returns The distinct values in first-seen order
 */
function distinct(values: string[]): string[] {
  return [...new Set(values)];
}

/**
 * Whether a raw source resolves at quote scope. Unknown keys are dropped, which
 * matches the renderer: it resolves them to null and draws nothing.
 * @param source - The raw source key
 * @returns True when the key is a quote-scope source
 */
function isKnownQuoteSource(source: string): boolean {
  return (
    isPrefixedSource(source, FP_CUSTOM_SOURCE_PREFIX) ||
    isPrefixedSource(source, FP_TOTAL_WEIGHT_SOURCE_PREFIX) ||
    FP_FIELD_SOURCES.some((candidate) => candidate === source)
  );
}

/**
 * Whether a raw source resolves at line scope.
 * @param source - The raw source key
 * @returns True when the key is a line-scope source
 */
function isKnownLineSource(source: string): boolean {
  return (
    isPrefixedSource(source, FP_CUSTOM_SOURCE_PREFIX) ||
    FP_LINE_FIELD_SOURCES.some((candidate) => candidate === source)
  );
}

/**
 * Whether a source carries a parameterized prefix with a non-empty parameter.
 * @param source - The raw source key
 * @param prefix - The source prefix to test for
 * @returns True when the key is prefixed and names something
 */
function isPrefixedSource(source: string, prefix: string): boolean {
  return source.startsWith(prefix) && source.slice(prefix.length).trim() !== "";
}

/**
 * Whether a `data-fp-show-when` condition is a named one rather than a field.
 * @param value - The condition, with any negation prefix already stripped
 * @returns True when the value names a built-in condition
 */
function isNamedCondition(value: string): boolean {
  return FP_SHOW_WHEN_CONDITIONS.some((candidate) => candidate === value);
}
